import { TRole } from 'redux/reducers/user/types';
import { IS_NEMAN } from 'services/constants/env';
import {
	EExchangeRatesTabGroup,
	EExchangeRatesTabGroupLabel,
	ETaxDutiesTabGroup,
	ETaxDutiesTabGroupLabel,
} from '../components/ReferenceTables/constants';

export enum ROLES {
	ADMIN = 'admin',
	DASHBOARD = 'dashboard',
	RESOURCES = 'resources',
	MONTHLY_FEE = 'monthly-fee',
	VAT_REPORTS = 'vat-reports',
	SWEDBANK_INTEGRATION = 'swed-bank-integration',
	HMRC = 'hmrc',
	ERROR_CORRECTION = 'error-correction',
	SWISSPOST_CLIENTS = 'swisspost-clients',
	VAT_REIMBURSEMENT = 'vat-reimbursement',
	SALES_PROCESSING = 'sales-processing',
	GPSR = 'gpsr',
	EUDR = 'eudr',
	SALES_REPORTS = 'sales-reports',
	REFERENCE_TABLES = 'reference-tables',
	EM_MANAGEMENT = 'em-management',
	REFERRALS = 'referrals',
	FISCAL_REPORTS = 'fiscal-reports',
	HS_CODES_RETRIEVING = 'hs-codes-retrieving',
	USER_MANAGEMENT = 'user-management',
	INVOICE_MANAGEMENT = 'invoice-management',
	ORDER_MANAGEMENT = 'order-management',
	EM_VAT_CORRECTIONS = 'vat-corrections',
	EU_DDP = 'eu-ddp',
	MASS_POST_SALE_JOBS = 'mass-post-sale-jobs',
	NON_EU_VAT_RATES = 'non-eu-vat-rates',
	PRP = 'prp',
	PPWR = 'ppwr',
	/**
	 * UI pseudo-role: not a CC role from `api/user`, it is added to the user's set when the access
	 * token carries the Keycloak realm role `paymentgateway_admin` (see `PAYMENT_GATEWAY_TOKEN_ROLE`).
	 */
	PAYMENT_GATEWAY_ADMIN = 'paymentgateway-admin',
}

/** Keycloak realm role (realm `operator`) that grants the Payment Gateway section. */
export const PAYMENT_GATEWAY_TOKEN_ROLE = 'paymentgateway_admin';

export const PATHS = {
	HOME_PAGE: `/`,
	LOGIN_PAGE: `/login`,
	SIGN_IN_PAGE: `/sign-in`,
	AUTH_CODE_PAGE: `/auth/code`,
	HS_CODES_RETRIEVING_PAGE: `/hs-code-retrieving`,
	MONTHLY_FEE_PAGE: `/${ROLES.MONTHLY_FEE}`,
	RESOURCES: `/${ROLES.RESOURCES}`,
	SWED_BANK_INTEGRATION_PAGE: `/${ROLES.SWEDBANK_INTEGRATION}`,
	VAT_REPORT_PAGE: `/${ROLES.VAT_REPORTS}`,
	VAT_REPORT_DOWNLOAD_PAGE: `/${ROLES.VAT_REPORTS}/:taskId`,
	VAT_REPORT_NEW_PAGE: `/${ROLES.VAT_REPORTS}/new`,
	DASHBOARD_PAGE: `/${ROLES.DASHBOARD}`,
	HMRC_PAGE: `/${ROLES.HMRC}`,
	HMRC_DETILES: `/${ROLES.HMRC}/:id`,
	ERROR_CORRECTIONS_PAGE: `/${ROLES.ERROR_CORRECTION}`,
	ERROR_CORRECTION_PAGE: `/${ROLES.ERROR_CORRECTION}/:errorId`,
	SWISS_POST_PAGE: `/${ROLES.SWISSPOST_CLIENTS}`,
	SWISS_POST_ITEM_PAGE: `/${ROLES.SWISSPOST_CLIENTS}/:orderId`,
	VAT_REIMBURSEMENT_MANAGEMENT_PAGE: `/${ROLES.VAT_REIMBURSEMENT}`,
	GPSR_PAGE: `/${ROLES.GPSR}`,
	EUDR_PAGE: `/${ROLES.EUDR}`,
	EUDR_USER_PAGE: `/${ROLES.EUDR}/:emId`,
	GPSR_PRODUCT_TYPE_PAGE: `/${ROLES.GPSR}/:emId`,
	GPSR_REGULATIONS_PAGE: `/${ROLES.GPSR}/:emId/:productTypeId`,
	GPSR_INVOICES_PAGE: `/${ROLES.GPSR}/:emId/invoices`,
	SALES_PROCESSING_PAGE: `/${ROLES.SALES_PROCESSING}`,
	REFERENCE_TABLES_PAGE: `/${ROLES.REFERENCE_TABLES}`,
	REFERENCE_TABLES_TAB_PAGE: `/${ROLES.REFERENCE_TABLES}/:tab`,
	EM_MANAGEMENT_PAGE: `/${ROLES.EM_MANAGEMENT}`,
	EM_MANAGEMENT_CREATE_PAGE: `/${ROLES.EM_MANAGEMENT}/create-em`,
	EM_VAT_CORRECTIONS_PAGE: `/${ROLES.EM_VAT_CORRECTIONS}`,
	EM_MANAGEMENT_ID_PAGE: `/${ROLES.EM_MANAGEMENT}/:emId`,
	EM_MANAGEMENT_CHANGE_HISTORY_PAGE: `/${ROLES.EM_MANAGEMENT}/:emId/change-history`,
	EM_MANAGEMENT_COMMUNICATION_HISTORY_PAGE: `/${ROLES.EM_MANAGEMENT}/:emId/communication-history`,
	EM_MANAGEMENT_COMMUNICATION_HISTORY_DETAIL_PAGE: `/${ROLES.EM_MANAGEMENT}/:emId/communication-history/:emailId`,
	EM_MANAGEMENT_SHOPIFY_STORE_DETAILS_PAGE: `/${ROLES.EM_MANAGEMENT}/:emId/shopify-store-details/:storeId`,

	REFERRALS_PAGE: `/${ROLES.REFERRALS}`,
	REFERRALS_PARTNERS_PAGE: `/${ROLES.REFERRALS}/partners`,
	REFERRALS_REPORTS_PAGE: `/${ROLES.REFERRALS}/reports`,
	FISCAL_REPORTS_PAGE: `/${ROLES.FISCAL_REPORTS}`,
	USER_MANAGEMENT_PAGE: `/${ROLES.USER_MANAGEMENT}`,
	TASK_TRACKER_PAGE: `/${ROLES.USER_MANAGEMENT}/task-tracker`,
	USER_MANAGEMENT_CHANGE_HISTORY_PAGE: `/${ROLES.USER_MANAGEMENT}/:userId/change-history`,
	INVOICE_MANAGEMENT_PAGE: `/${ROLES.INVOICE_MANAGEMENT}`,
	INVOICE_MANAGEMENT_CREDIT_NOTES_PAGE: `/${ROLES.INVOICE_MANAGEMENT}/credit-notes`,
	INVOICE_MANAGEMENT_CREDIT_NOTE_DETAILS_PAGE: `/${ROLES.INVOICE_MANAGEMENT}/credit-notes/:creditNoteId`,
	INVOICE_MANAGEMENT_ID_PAGE: `/${ROLES.INVOICE_MANAGEMENT}/:invoiceNumber`,
	ORDER_MANAGEMENT_PAGE: `/${ROLES.ORDER_MANAGEMENT}`,
	ORDER_MANAGEMENT_DETAIL_PAGE: `/${ROLES.ORDER_MANAGEMENT}/:orderId`,
	EU_DDP_ORDERS_PAGE: `/${ROLES.EU_DDP}`,
	EU_DDP_PRINT_LABELS_PAGE: `/${ROLES.EU_DDP}/print-labels`,
	EU_DDP_ORDER_DETAIL_PAGE: `/${ROLES.EU_DDP}/:orderId`,
	SALES_REPORTS_PAGE: `/${ROLES.SALES_REPORTS}`,
	MASS_POST_SALE_JOBS_PAGE: `/${ROLES.MASS_POST_SALE_JOBS}`,
	MASS_POST_SALE_JOB_ORDERS_PAGE: `/${ROLES.MASS_POST_SALE_JOBS}/:jobId/:processingType`,
	DOWNLOAD_INVOICE_ARCHIVE_PAGE: `/download-invoice-archive`,
	PRP_PAGE: `/${ROLES.PRP}`,
	PPWR_PAGE: `/${ROLES.PPWR}`,
	PPWR_REGISTRATION_FORMS_PAGE: `/${ROLES.PPWR}/registration-forms`,
	PPWR_SETTINGS_PAGE: `/${ROLES.PPWR}/:emId`,
	PAYMENT_GATEWAY_PAGE: '/payment-gateway',
	PAYMENT_GATEWAY_TAB_PAGE: '/payment-gateway/:tab',
	REPORTING_CENTER_PAGE: '/reporting-center',
};

export const DEFAULT_AUTHORIZED_PATH = PATHS.EM_MANAGEMENT_PAGE;

export enum ENavigateNames {
	DASHBOARD = 'Dashboards',
	RESOURCES = 'IT Directory',
	MONTHLY_FEE = 'Monthly Fee Change Requests',
	VAT_REPORTS = 'VAT Reports',
	SWEDBANK_INTEGRATION = 'SwedBank Integration',
	HMRC = 'HMRC VAT Reports',
	ERROR_CORRECTION = 'Error Correction',
	SWISSPOST_CLIENTS = 'SwissPost Clients',
	VAT_REIMBURSEMENT = 'VAT Reimbursement',
	SALES_PROCESSING = 'CSV Uploads Processing',
	GPSR = 'GPSR',
	EUDR = 'EUDR',
	REFERENCE_TABLES = 'Reference Tables',
	NON_EU_VAT_RATES = 'NON EU VAT Rates',
	EU_VAT_RATES = 'EU VAT Rates',
	HS6_PLUS_CODES = 'HS6+ Codes',
	EU_CUSTOMS_DUTY_RATES = 'EU Customs Duty Rates',
	EU_SPECIAL_VAT_TERRITORIES = 'EU Special VAT Territories',
	SHOPIFY_SALES_CHANNELS = 'Shopify Sales Channels',
	EM_MANAGEMENT = 'EM Management',
	REFERRALS = 'Referral Hub',
	REFERRALS_PARTNERS = 'Referrals Partners',
	FISCAL_REPORTS = 'Fiscal Reports',
	HS_CODES_RETRIEVING = 'HS Code Retrieval',
	USER_MANAGEMENT = 'User Management',
	INVOICE_MANAGEMENT = 'Invoice Management',
	INVOICES = 'Invoices',
	CREDIT_NOTES = 'Credit Notes',
	ORDER_MANAGEMENT = 'Order Management',
	EM_VAT_CORRECTIONS = 'EM VAT Corrections',
	SALES_REPORTS = 'EM Standard Sales Reports',
	EU_DDP_ORDERS = 'EU DDP Orders',
	MASS_POST_SALE_JOBS = 'Mass Post Sale Jobs',
	PRP = 'PRP',
	PPWR = 'PPWR',
	PAYMENT_GATEWAY = 'Payment Gateway',
	REPORTING_CENTER = 'Reporting Center',
}

export enum ENavGroupNames {
	FINANCE_VAT = 'Finance & VAT',
	OPERATIONS = 'Operations',
	EXTRA_SERVICES = 'Extra Services',
	RESOURCES = 'Resources',
}

export type TNavItem = {
	name: string;
	path: string;
	roles: ROLES[];
	/**
	 * Keycloak realm role the access token must carry. Checked before the `admin` bypass: a CC admin
	 * without the role does not see the item (the API would answer 403 anyway).
	 */
	requiresTokenRole?: string;
	/** Hidden on Neman (no Keycloak there, and the gateway does not serve it). */
	hideOnNeman?: boolean;
};

export type TNavGroup = {
	name: string;
	items: TNavEntry[];
};

export type TNavEntry = TNavItem | TNavGroup;

export const isNavGroup = (entry: TNavEntry): entry is TNavGroup =>
	(entry as TNavGroup).items !== undefined;

export const NAVIGATE_GROUPED: TNavEntry[] = [
	{
		name: ENavigateNames.DASHBOARD,
		path: PATHS.DASHBOARD_PAGE,
		roles: [ROLES.ADMIN, ROLES.DASHBOARD],
	},
	{
		name: ENavigateNames.EM_MANAGEMENT,
		path: PATHS.EM_MANAGEMENT_PAGE,
		roles: [ROLES.ADMIN, ROLES.EM_MANAGEMENT],
	},
	{
		name: ENavigateNames.ORDER_MANAGEMENT,
		path: `${PATHS.ORDER_MANAGEMENT_PAGE}?defaultFilter=true`,
		roles: [ROLES.ADMIN, ROLES.ORDER_MANAGEMENT],
	},
	{
		name: ENavigateNames.INVOICE_MANAGEMENT,
		items: [
			{
				name: ENavigateNames.INVOICES,
				path: PATHS.INVOICE_MANAGEMENT_PAGE,
				roles: [ROLES.ADMIN, ROLES.INVOICE_MANAGEMENT, ROLES.EM_MANAGEMENT],
			},
			{
				name: ENavigateNames.CREDIT_NOTES,
				path: PATHS.INVOICE_MANAGEMENT_CREDIT_NOTES_PAGE,
				roles: [ROLES.ADMIN, ROLES.INVOICE_MANAGEMENT, ROLES.EM_MANAGEMENT],
			},
		],
	},
	{
		name: ENavGroupNames.FINANCE_VAT,
		items: [
			{
				name: ENavigateNames.FISCAL_REPORTS,
				path: `${PATHS.FISCAL_REPORTS_PAGE}?defaultFilter=true`,
				roles: [ROLES.ADMIN, ROLES.FISCAL_REPORTS],
			},
			{
				name: ENavigateNames.EM_VAT_CORRECTIONS,
				path: `${PATHS.EM_VAT_CORRECTIONS_PAGE}?defaultFilter=true`,
				roles: [ROLES.ADMIN, ROLES.EM_VAT_CORRECTIONS, ROLES.EM_MANAGEMENT],
			},
			{
				name: ENavigateNames.VAT_REIMBURSEMENT,
				path: PATHS.VAT_REIMBURSEMENT_MANAGEMENT_PAGE,
				roles: [ROLES.ADMIN, ROLES.VAT_REIMBURSEMENT],
			},
			{
				name: ENavigateNames.HMRC,
				path: PATHS.HMRC_PAGE,
				roles: [ROLES.ADMIN, ROLES.HMRC],
			},
			{
				name: ENavigateNames.SWEDBANK_INTEGRATION,
				path: PATHS.SWED_BANK_INTEGRATION_PAGE,
				roles: [ROLES.ADMIN, ROLES.SWEDBANK_INTEGRATION],
			},
			{
				name: ENavigateNames.VAT_REPORTS,
				path: PATHS.VAT_REPORT_NEW_PAGE,
				roles: [ROLES.ADMIN, ROLES.VAT_REPORTS],
			},
			{
				name: ENavigateNames.REPORTING_CENTER,
				path: PATHS.REPORTING_CENTER_PAGE,
				roles: [ROLES.ADMIN, ROLES.FISCAL_REPORTS, ROLES.VAT_REPORTS],
			},
			{
				name: ENavigateNames.PAYMENT_GATEWAY,
				path: PATHS.PAYMENT_GATEWAY_PAGE,
				roles: [ROLES.PAYMENT_GATEWAY_ADMIN],
				requiresTokenRole: PAYMENT_GATEWAY_TOKEN_ROLE,
				hideOnNeman: true,
			},
		],
	},
	{
		name: ENavGroupNames.OPERATIONS,
		items: [
			{
				name: ENavigateNames.MONTHLY_FEE,
				path: PATHS.MONTHLY_FEE_PAGE,
				roles: [ROLES.ADMIN, ROLES.MONTHLY_FEE],
			},
			{
				name: ENavigateNames.SALES_REPORTS,
				path: PATHS.SALES_REPORTS_PAGE,
				roles: [ROLES.ADMIN, ROLES.SALES_REPORTS],
			},
			{
				name: ENavigateNames.ERROR_CORRECTION,
				path: PATHS.ERROR_CORRECTIONS_PAGE,
				roles: [ROLES.ADMIN, ROLES.ERROR_CORRECTION],
			},
			{
				name: ENavigateNames.HS_CODES_RETRIEVING,
				path: PATHS.HS_CODES_RETRIEVING_PAGE,
				roles: [ROLES.ADMIN, ROLES.HS_CODES_RETRIEVING],
			},
			{
				name: ENavigateNames.SALES_PROCESSING,
				path: PATHS.SALES_PROCESSING_PAGE,
				roles: [ROLES.ADMIN, ROLES.SALES_PROCESSING],
			},
			{
				name: ENavigateNames.MASS_POST_SALE_JOBS,
				path: PATHS.MASS_POST_SALE_JOBS_PAGE,
				roles: [ROLES.ADMIN, ROLES.MASS_POST_SALE_JOBS, ROLES.SALES_PROCESSING],
			},
		],
	},
	{
		name: ENavGroupNames.EXTRA_SERVICES,
		items: [
			{
				name: ENavigateNames.GPSR,
				path: PATHS.GPSR_PAGE,
				roles: [ROLES.ADMIN, ROLES.GPSR],
			},
			{
				name: ENavigateNames.SWISSPOST_CLIENTS,
				path: PATHS.SWISS_POST_PAGE,
				roles: [ROLES.ADMIN, ROLES.SWISSPOST_CLIENTS],
			},
			{
				name: ENavigateNames.REFERRALS,
				path: PATHS.REFERRALS_PAGE,
				roles: [ROLES.ADMIN, ROLES.REFERRALS],
			},
			{
				name: ENavigateNames.EUDR,
				path: PATHS.EUDR_PAGE,
				roles: [ROLES.ADMIN, ROLES.EUDR],
			},
			{
				name: ENavigateNames.EU_DDP_ORDERS,
				path: PATHS.EU_DDP_ORDERS_PAGE,
				roles: [ROLES.ADMIN, ROLES.EU_DDP],
			},
			{
				name: ENavigateNames.PRP,
				path: PATHS.PRP_PAGE,
				roles: [ROLES.ADMIN, ROLES.PRP],
			},
			{
				name: ENavigateNames.PPWR,
				path: PATHS.PPWR_PAGE,
				roles: [ROLES.ADMIN, ROLES.PPWR],
			},
		],
	},
	{
		name: ENavGroupNames.RESOURCES,
		items: [
			{
				name: ENavigateNames.REFERENCE_TABLES,
				items: [
					{
						name: ENavigateNames.REFERENCE_TABLES,
						path: PATHS.REFERENCE_TABLES_PAGE,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: ETaxDutiesTabGroupLabel.NON_EU_VAT_RATES,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/${ETaxDutiesTabGroup.NON_EU_VAT_RATES}`,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: ETaxDutiesTabGroupLabel.EU_VAT_RATES,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/${ETaxDutiesTabGroup.EU_VAT_RATES}`,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: ETaxDutiesTabGroupLabel.HS6P_CODES,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/${ETaxDutiesTabGroup.HS6P_CODES}`,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: ETaxDutiesTabGroupLabel.EU_CUSTOMS_DUTY_RATES,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/${ETaxDutiesTabGroup.EU_CUSTOMS_DUTY_RATES}`,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: ETaxDutiesTabGroupLabel.EU_SPECIAL_VAT_TERRITORIES,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/${ETaxDutiesTabGroup.EU_SPECIAL_VAT_TERRITORIES}`,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: EExchangeRatesTabGroupLabel.EU_MONTHLY_EXCHANGE_RATES,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/${EExchangeRatesTabGroup.EU_MONTHLY_EXCHANGE_RATES}`,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: EExchangeRatesTabGroupLabel.ANNUAL_EXCHANGE_RATES,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/${EExchangeRatesTabGroup.ANNUAL_EXCHANGE_RATES}`,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: EExchangeRatesTabGroupLabel.DAILY_EXCHANGE_RATES,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/${EExchangeRatesTabGroup.DAILY_EXCHANGE_RATES}`,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: EExchangeRatesTabGroupLabel.GBP_MONTHLY_EXCHANGE_RATES,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/${EExchangeRatesTabGroup.GBP_MONTHLY_EXCHANGE_RATES}`,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: EExchangeRatesTabGroupLabel.UK_EXCHANGE_RATES,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/${EExchangeRatesTabGroup.UK_EXCHANGE_RATES}`,
						roles: [ROLES.ADMIN, ROLES.REFERENCE_TABLES, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
					},
					{
						name: ENavigateNames.SHOPIFY_SALES_CHANNELS,
						path: `${PATHS.REFERENCE_TABLES_PAGE}/shopify-sales-channels`,
						roles: [
							ROLES.ADMIN,
							ROLES.REFERENCE_TABLES,
							ROLES.RESOURCES,
							ROLES.ORDER_MANAGEMENT,
							ROLES.EM_MANAGEMENT,
						],
					},
				],
			},
			{
				name: ENavigateNames.RESOURCES,
				path: PATHS.RESOURCES,
				roles: [ROLES.ADMIN, ROLES.RESOURCES, ROLES.ORDER_MANAGEMENT],
			},
		],
	},
];

// Recursively collects leaf items (entries with a `path`) from a nav tree.
export const flattenNavItems = (entries: TNavEntry[]): TNavItem[] =>
	entries.flatMap((entry) => (isNavGroup(entry) ? flattenNavItems(entry.items) : [entry]));

// Flat list of nav items derived from the grouped structure.
// Used by any consumer that needs a non-grouped list.
export const NAVIGATE: TNavItem[] = flattenNavItems(NAVIGATE_GROUPED);

// Items gated by a token role or hidden on Neman: the gate is checked before the admin bypass.
const isGatedItem = (item: TNavItem): boolean => !!item.requiresTokenRole || !!item.hideOnNeman;

const passesGate = (item: TNavItem, tokenRoles: ReadonlySet<string>): boolean =>
	!(item.hideOnNeman && IS_NEMAN) &&
	(!item.requiresTokenRole || tokenRoles.has(item.requiresTokenRole));

// CC roles (`roleNameUI`) plus the UI pseudo-roles derived from the token's realm roles.
const buildUserRoles = (
	roles: TRole[] | undefined,
	tokenRoles: ReadonlySet<string>,
): Set<string> => {
	const userRoles = new Set<string>((roles ?? []).map((role) => role.roleNameUI));
	if (tokenRoles.has(PAYMENT_GATEWAY_TOKEN_ROLE)) userRoles.add(ROLES.PAYMENT_GATEWAY_ADMIN);
	return userRoles;
};

const filterNavEntries = (
	entries: TNavEntry[],
	userRoles: Set<string>,
	tokenRoles: ReadonlySet<string>,
): TNavEntry[] => {
	const isAdmin = userRoles.has(ROLES.ADMIN);
	return entries.flatMap((entry): TNavEntry[] => {
		if (isNavGroup(entry)) {
			const items = filterNavEntries(entry.items, userRoles, tokenRoles);
			return items.length > 0 ? [{ ...entry, items }] : [];
		}
		if (!passesGate(entry, tokenRoles)) return [];
		return isAdmin || entry.roles.some((role) => userRoles.has(role)) ? [entry] : [];
	});
};

// Returns the grouped navigation structure filtered by user permissions.
// A group (at any depth) is omitted entirely when none of its items are allowed.
// `tokenRoles` are the Keycloak realm roles of the access token (`auth.realmRoles`).
export const filterNavGrouped = (roles: TRole[], tokenRoles: string[] = []): TNavEntry[] => {
	const tokenRoleSet = new Set(tokenRoles);
	return filterNavEntries(NAVIGATE_GROUPED, buildUserRoles(roles, tokenRoleSet), tokenRoleSet);
};

export const filterNav = (roles: TRole[], tokenRoles: string[] = []): TNavItem[] =>
	flattenNavItems(filterNavGrouped(roles, tokenRoles));

const stripQuery = (path: string): string => path.split('?')[0];

const isSameOrNestedPath = (target: string, base: string): boolean =>
	target === base || target.startsWith(`${base}/`);

// Whether the user may access `path` (without query string) according to the
// nav tree: a path under a gated item (token role, Neman) needs the gate to pass,
// admin included; otherwise admin always may, and anyone else needs a nav item
// with that exact path that lists one of the user's roles.
export const isPathAllowed = (
	path: string,
	roles: TRole[] | undefined,
	tokenRoles: string[] = [],
): boolean => {
	const tokenRoleSet = new Set(tokenRoles);
	const userRoles = buildUserRoles(roles, tokenRoleSet);
	const isAdmin = userRoles.has(ROLES.ADMIN);
	const target = stripQuery(path);

	const gatedItems = NAVIGATE.filter(
		(item) => isGatedItem(item) && isSameOrNestedPath(target, stripQuery(item.path)),
	);
	if (gatedItems.length > 0) {
		return gatedItems.some(
			(item) =>
				passesGate(item, tokenRoleSet) &&
				(isAdmin || item.roles.some((role) => userRoles.has(role))),
		);
	}

	if (isAdmin) return true;
	return NAVIGATE.some(
		(item) => stripQuery(item.path) === target && item.roles.some((role) => userRoles.has(role)),
	);
};
